import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { TipoTransacao } from '../../../enums/TipoTransacao.enum';
import { Moeda } from '../../../enums/moeda.enum';
import { TransferenciaDTO } from '../../../models/gerenciar-contas/transferencia.model';
import { ContaDTO } from '../../../models/gerenciar-contas/conta.model';
import { TransferenciaService } from '../../../services/movimento/transferencia.service';
import { ContaService } from '../../../services/conta/conta.service';
import { CotacaoGuardService } from '../../../services/cotacao/cotacao-guard.service';
import { CotacaoService } from '../../../services/cotacao/cotacao.service';
import { EmpresaConfigService } from '../../../services/configuracoes/empresa.service';
import { EmpresaConfigDTO } from '../../../models/configuracoes/empresa.model';
import { moedasCompativeis } from '../../../utils/moeda-pareamento.util';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-nova-transferencia-edit',
  templateUrl: './nova-transferencia-edit.component.html',
  styleUrls: ['./nova-transferencia-edit.component.scss'],
  providers: [MessageService],
})
export class NovaTransferenciaEditComponent implements OnInit, OnDestroy {
  form: TransferenciaDTO = this.createEmptyForm();
  contas: ContaDTO[] = [];
  contasOptions: { label: string; value: number; moeda: string; flagUrl: string; tipoConta: string }[] = [];
  moedas: { label: string; value: any; flagUrl?: string }[] = [];
  idEdit?: number;
  dataSelecionada: string = 'hoje';
  empresa: EmpresaConfigDTO | null = null;

  // Objetos de traducao
  texts = {
    titulo: '',
    observacao: '',
    conta_origem: '',
    conta_destino: '',
    moeda_de_origem: '',
    moeda_de_destino: '',
    valor: '',
    cotacao_de_transferencia: '',
    valor_convertido: ''
  };

  placeholders = {
    observacao: '',
    selecione: '',
    valor: '',
    cotacao: '',
    valor_convertido: ''
  };

  botoes = {
    salvar: ''
  };

  private langChangeSub!: Subscription;

  constructor(
    private transferenciaService: TransferenciaService,
    private contaService: ContaService,
    private cotacaoService: CotacaoService,
    private cotacaoGuard: CotacaoGuardService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private empresaService: EmpresaConfigService
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      // Re-traduz labels do dropdown de moeda ao trocar idioma
      this.buildMoedaOptions();
    });
    // Monta dropdown ja com fallback (todas as 4 moedas) ate empresa carregar
    this.buildMoedaOptions();
    this.loadContas();
    this.findEmpresa();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.idEdit = +id;
      this.loadTransferencia(this.idEdit);
    }
    this.definirDataHoje();
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('nova-transferencia-edit.texts.titulo'),
      observacao: this.translate.instant('nova-transferencia-edit.labels.observacao'),
      conta_origem: this.translate.instant('nova-transferencia-edit.labels.conta_origem'),
      conta_destino: this.translate.instant('nova-transferencia-edit.labels.conta_destino'),
      moeda_de_origem: this.translate.instant('nova-transferencia-edit.labels.moeda_de_origem'),
      moeda_de_destino: this.translate.instant('nova-transferencia-edit.labels.moeda_de_destino'),
      valor: this.translate.instant('nova-transferencia-edit.labels.valor'),
      cotacao_de_transferencia: this.translate.instant('nova-transferencia-edit.labels.cotacao_de_transferencia'),
      valor_convertido: this.translate.instant('nova-transferencia-edit.labels.valor_convertido')
    };

    this.placeholders = {
      observacao: this.translate.instant('nova-transferencia-edit.placeholders.observacao'),
      selecione: this.translate.instant('nova-transferencia-edit.placeholders.selecione'),
      valor: this.translate.instant('nova-transferencia-edit.placeholders.valor'),
      cotacao: this.translate.instant('nova-transferencia-edit.placeholders.cotacao'),
      valor_convertido: this.translate.instant('nova-transferencia-edit.placeholders.valor_convertido')
    };

    this.botoes = {
      salvar: this.translate.instant('nova-transferencia-edit.botoes.salvar')
    };
  }

  formatContaOptions(): void {
    this.contasOptions = this.contas
      .filter((c) => c.id !== undefined)
      .map((c) => {
        const moedaStr = String(c.moeda || '').toUpperCase();
        return {
          label: (c.numeroConta as any) == 0 ? `${c.tituloDaConta} - EFETIVO` : c.tituloDaConta,
          value: c.id as number,
          moeda: moedaStr,
          flagUrl: this.getFlagUrl(c.moeda),
          tipoConta: String(c.tipoConta || ''),
        };
      });

    // Ordenar: conta DINHEIRO da moeda padrao da empresa aparece primeiro
    if (this.empresa?.moeda) {
      const moedaPadrao = this.asEnumString(this.empresa.moeda);
      this.contasOptions.sort((a, b) => {
        const contaA = this.contas.find(c => c.id === a.value);
        const contaB = this.contas.find(c => c.id === b.value);
        const isTopA = contaA && this.asEnumString(contaA.tipoConta) === 'DINHEIRO' && this.asEnumString(contaA.moeda) === moedaPadrao ? 1 : 0;
        const isTopB = contaB && this.asEnumString(contaB.tipoConta) === 'DINHEIRO' && this.asEnumString(contaB.moeda) === moedaPadrao ? 1 : 0;
        return isTopB - isTopA;
      });
    }
  }

  private asEnumString(v: any): string {
    if (!v) return '';
    if (typeof v === 'object' && 'enum' in v) return String(v.enum).toUpperCase();
    if (typeof v === 'object' && 'value' in v) return String(v.value).toUpperCase();
    return String(v ?? '').toUpperCase();
  }

  /**
   * Conjunto de moedas permitidas para a empresa logada, baseado na
   * matriz de pareamento (moedasCompativeis). Fallback: enquanto a
   * empresa nao carregou, retorna todas as 4 para nao bloquear UI.
   *
   * Nota: aplicado tanto em moeda de origem quanto destino. Validacao
   * fina de pareamento entre origem/destino fica para evolucao posterior
   * (atualmente ambos os dropdowns usam o mesmo `moedas`).
   */
  get moedasPermitidas(): Set<string> {
    const moedaEmp = this.empresa?.moeda
      ? ((this.empresa.moeda as any).enum ?? this.empresa.moeda) as string
      : null;
    if (!moedaEmp) return new Set(['GUARANI', 'DOLAR', 'REAL', 'PESO']);
    return new Set(moedasCompativeis(moedaEmp as any));
  }

  /**
   * Monta o dropdown de moeda aplicando o filtro de pareamento.
   * Edge case legacy: ao editar transferencia com moeda agora bloqueada,
   * mantem as moedas atuais (origem/destino) visiveis para nao "sumir"
   * o valor selecionado.
   */
  private buildMoedaOptions(): void {
    const permitidas = this.moedasPermitidas;
    const moedaOrigemAtual = this.form?.moedaDeOrigem
      ? this.asEnumString((this.form.moedaDeOrigem as any).value ?? this.form.moedaDeOrigem)
      : null;
    const moedaDestinoAtual = this.form?.moedaDeDestino
      ? this.asEnumString((this.form.moedaDeDestino as any).value ?? this.form.moedaDeDestino)
      : null;
    this.moedas = Object.values(Moeda)
      .filter(m => {
        const codigo = this.asEnumString(m);
        return permitidas.has(codigo)
          || codigo === moedaOrigemAtual
          || codigo === moedaDestinoAtual;
      })
      .map(moeda => ({
        label: this.translate.instant(`enums.moeda.${moeda.toLowerCase()}`),
        value: moeda,
        flagUrl: this.getFlagUrl(moeda),
      }));
  }

  findEmpresa(): void {
    this.empresaService.getById('/configuracaoEmpresa/sessao').subscribe({
      next: (response) => {
        this.empresa = response;
        // Re-monta dropdown de moeda agora que `moedasPermitidas` reflete a
        // moeda padrao da empresa (sem fallback para todas as 4).
        this.buildMoedaOptions();
        // Re-ordenar contas caso ja tenham sido carregadas antes da empresa
        if (this.contasOptions.length > 0) {
          this.formatContaOptions();
        }
      },
      error: () => {
        // Mantem fallback (todas as 4) quando empresa nao carrega
        this.buildMoedaOptions();
      }
    });
  }

  private getMoedaLabel(moeda: any): string {
    const moedaStr = String(moeda || '').toUpperCase();
    const moedaMap: { [key: string]: string } = {
      'GUARANI': 'Guarani',
      'REAL': 'Real',
      'DOLAR': 'Dolar'
    };
    return moedaMap[moedaStr] || '';
  }

  getFlagUrl(moeda: any): string {
    const moedaStr = String(moeda || '').toUpperCase();
    switch (moedaStr) {
      case 'GUARANI': return 'assets/images/paraguay-flag.jpg';
      case 'REAL': return 'assets/images/br-flag.svg';
      case 'DOLAR': return 'assets/images/usa-flag.svg';
      case 'PESO': return 'assets/images/argentina-flag.svg';
      default: return '';
    }
  }

  /**
   * Monta o label da conta no dropdown.
   * Segue o mesmo padrao da tela de movimentacao (extrato de caixa):
   * numeroConta == 0 => "EFETIVO - {tituloDaConta}"
   * Caso contrario => tituloDaConta
   */
  private buildContaLabel(conta: ContaDTO): string {
    return (conta.numeroConta as any) == 0
      ? `EFETIVO - ${conta.tituloDaConta}`
      : conta.tituloDaConta;
  }

  loadTransferencia(id: number): void {
    this.transferenciaService.getById(`/movimento/${id}`).subscribe({
      next: (response) => {
        this.form = {
          ...response,
          conta_id: response.contaDTO?.id,
          contaDestino_id: response.contaDestinoDTO?.id,
          moedaDeOrigem: response.moedaDeOrigem,
          moedaDeDestino: response.moedaDeDestino,
        };
        // Garante que as moedas atuais (legacy) entrem na lista mesmo se bloqueadas
        this.buildMoedaOptions();
      },
      error: () => {
        this.router.navigate(['/transferencias']);
      },
    });
  }

  loadContas(): void {
    this.contaService.get('/conta').subscribe({
      next: (response) => {
        this.contas = response.content;
        this.formatContaOptions();
      },
      error: (err) => {
        console.error('Erro ao carregar contas:', err);
      },
    });
  }

  submit(): void {
    if (!this.validateForm()) {
      return;
    }

    // Validacao: conta origem e destino nao podem ser iguais
    if (this.form.conta_id && this.form.conta_id === this.form.contaDestino_id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('nova-transferencia.mensagens.mesma_conta'),
      });
      return;
    }

    this.loadCotacao(() => {
      const body: TransferenciaDTO = {
        ...this.form,
        tipoDeTransacao: TipoTransacao.TRANSFERENCIA,
        cancelado: false,
        baixas: [],
       // emissao: new Date().toISOString(),
      };

      this.transferenciaService.put(`/movimento/${this.idEdit}`, body).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: this.translate.instant('nova-transferencia-edit.mensagens.editada_sucesso'),
          });
          this.router.navigate(['/nova-transferencia']);
        },
      });
    });
  }

  /** Garante a cotacao do dia (solicita ao usuario se nao houver) antes de salvar. Cancelou: nao salva. */
  loadCotacao(callback?: () => void): void {
    if (!this.form.moedaDeOrigem) {
      callback?.();
      return;
    }
    this.cotacaoGuard.garantirCotacao().then((cotacao) => {
      if (!cotacao?.id) {
        return;
      }
      this.form.cotacao_id = cotacao.id;
      callback?.();
    });
  }

  loadCotacaoDeTransferencia(callback?: () => void): void {
    if (!this.form.moedaDeDestino) {
      callback?.();
      return;
    }
    // moedaDeDestino pode ser string (vinda do backend) ou objeto { label, value } (selecionado no dropdown)
    const moedaDestino = typeof this.form.moedaDeDestino === 'string'
      ? this.form.moedaDeDestino
      : this.form.moedaDeDestino.value;
    if (!moedaDestino) {
      callback?.();
      return;
    }
    this.cotacaoService.getCotacaoAtual(moedaDestino).subscribe({
      next: (response) => {
        if (response.id !== undefined) {
          this.form.cotacaoDeTransferencia = response.id;
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: this.translate.instant('nova-transferencia-edit.mensagens.cotacao_transferencia_nao_encontrada'),
          });
        }
        callback?.();
      },
    });
  }


  validateForm(): boolean {
    const requiredFields: (keyof TransferenciaDTO)[] = [
      'observacao',
      'valor',
      'moedaDeOrigem',
      'moedaDeDestino',
      'conta_id',
      'contaDestino_id',
    ];
    for (const field of requiredFields) {
      if (!this.form[field]) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: `O campo ${field} é obrigatório.`,
        });
        return false;
      }
    }
    return true;
  }

  private createEmptyForm(): TransferenciaDTO {
    return {
      observacao: '',
      tipoDeTransacao: TipoTransacao.TRANSFERENCIA,
      cancelado: false,
      valor: 0,
      cotacao_id: 0,
      conta_id: undefined,
      pessoa_id: null,
      moedaDeOrigem: undefined,
      moedaDeDestino: undefined,
      contaDestino_id: undefined,
      cotacaoDeTransferencia: 0,
      valorConvertido: 0,
      emissao: undefined,
      baixas: [],
      valorRecebidoNaConta: 0,
      valorRecebidoEmGuarani: 0,
      valorRecebidoEmDolar: 0,
      valorRecebidoEmReal: 0,
      // Spec 06-financeiro-multimoeda-fix: payload preparado para ARS;
      // backend ja aceita o campo (T0 confirmou DTO + entity + migracao V1.56).
      valorRecebidoEmPeso: 0,
    };
  }

    selecionarData(tipo: string): void {
    this.dataSelecionada = tipo;

    const hoje = new Date();

    switch (tipo) {
      case 'hoje':
        this.form.emissao = new Date();
        break;
      case 'ontem':
        const ontem = new Date(hoje);
        ontem.setDate(hoje.getDate() - 1);
        this.form.emissao = ontem;
        break;
      case 'outros':
        // Mantém a data atual ou deixa vazio para o usuário selecionar
        if (!this.form.emissao) {
          this.form.emissao = new Date();
        }
        break;
    }

    // Atualiza o formulário principal se necessário
    if (this.form.emissao) {
      this.form.emissao = this.formatarDataParaAPI(this.form.emissao);
    }
  }

  abrirCalendario(): void {
    this.dataSelecionada = 'outros';
    // Foca no calendário quando clicar em "Outros"
    setTimeout(() => {
      const calendarElement = document.querySelector('p-calendar') as any;
      if (calendarElement && calendarElement.toggle) {
        calendarElement.toggle();
      }
    }, 0);
  }

  onDataSelecionada(): void {
    // Quando seleciona uma data manualmente no calendário
    if (this.form.emissao) {
      this.form.emissao = this.formatarDataParaAPI(this.form.emissao);
    }
  }

  onDataChange() {
    if (this.dataSelecionada === 'hoje') {
      this.form.emissao = new Date();
    } else if (this.dataSelecionada === 'ontem') {
      const ontem = new Date();
      ontem.setDate(ontem.getDate() - 1);
      this.form.emissao = ontem;
    } else {
      // outros → deixa o campo visível e vazio
      this.form.emissao = undefined;
    }
  }


  private definirDataHoje(): void {
    this.form.emissao = new Date();
    this.form.emissao = this.formatarDataParaAPI(this.form.emissao);
  }

  // Preserva a data escolhida pelo usuario e usa a hora atual (alinhado com
  // nova-receita/nova-despesa). Antes esta funcao zerava a hora; com a
  // padronizacao do helper de submit em `toLocalISOString` (spec 12), a
  // composicao da hora atual passa a acontecer aqui.
  private formatarDataParaAPI(data: Date): Date {
    const d = data instanceof Date ? data : new Date(data);
    const agora = new Date();
    return new Date(
      d.getFullYear(), d.getMonth(), d.getDate(),
      agora.getHours(), agora.getMinutes(), agora.getSeconds()
    );
  }
}
